import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API, graphqlOperation, Storage, Auth } from "aws-amplify";
import { getCandidateProfile } from "../graphql/queries";
import awsExports from "../aws-exports";
import Header from "../components/header/Header";
import { updateCandidateProfile } from "../graphql/mutations";
import { Formik } from 'formik';
import * as Yup from 'yup';
import { RiseLoader } from "react-spinners";

API.configure(awsExports);
Auth.configure(awsExports);
Storage.configure(awsExports);

const EditCandidateProfileReg = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [candidateProfileId, setCandidateProfileId] = useState(
    localStorage.getItem("candidateProfileId") || ""
  );

  const [candidateProfile, setCandidateProfile] = useState({
    fullName: "",
    gender: "",
    mobileNumber: "",
    description: "",
    matchedPercentage: 0,
    workStatus: "",
    currentCity: "",
    educationDetails: "",
    keySkills: "",
    workPreference: "",
    preferredSalary: "",
    photoURL: "",
    resumeURL: "",
    roleApplyFor: "",
    address: "",
    workMode: [],
    workCategory: [],
    githubURL: "",
    linkedinURL: "",
  });

  useEffect(() => {
    if (candidateProfileId) {
      const fetchCandidateProfile = async () => {
        setLoading(true);
        try {
          const response = await API.graphql(
            graphqlOperation(getCandidateProfile, {
              id: candidateProfileId,
            })
          );
          const profileData = response.data.getCandidateProfile;
          setCandidateProfile(profileData);
        } catch (error) {
          console.error("Error fetching candidate profile:", error);
        }
        setLoading(false);
      };
      fetchCandidateProfile();
    }
  }, [candidateProfileId]);

  const signupschema = Yup.object({
    fullName: Yup.string()
      .matches(/^[A-Za-z\s]+$/, "Full name must contain only letters")
      .min(2, "Please Enter valid Name")
      .max(25, "Full name must not exceed 25 characters"),
    mobileNumber: Yup.string()
      .matches(/^[0-9]{10}$/, 'Mobile Number must be exactly 10 digits'),
    workStatus: Yup.string()
      .matches(/^[A-Za-z\s]+$/, "must contain only letters"),
    roleApplyFor: Yup.string()
      .matches(/^[A-Za-z.,\s\-]+$/, 'Only letters, spaces, periods, commas, and hyphens are allowed'),
    workPreference: Yup.string()
      .matches(/^[A-Za-z\s]+$/, "must contain only letters"),
    currentCity: Yup.string()
      .matches(/^[A-Za-z\s]+$/, "must contain only letters"),
    keySkills: Yup.string()
      .required('Skills must be there')
      .matches(/^[a-zA-Z\s,.\\-]+$/, 'Only letters, spaces, commas, periods, and hyphens are allowed'),
    preferredSalary: Yup.string()
      .matches(/^[0-9]+$/, 'Valid salary'),
    description: Yup.string()
      .matches(/^[a-zA-Z0-9\s,.']+$/, 'Only letters and numbers are allowed'),
    resume: Yup.mixed()
      .test('fileType', 'Only PDF files are allowed', (value) =>
        value ? value.type === 'application/pdf' : true
      )
      .test('fileSize', 'File size should be between 5 KB and 25 MB', (value) =>
        value ? value.size >= 5 * 1024 && value.size <= 5 * 1024 * 1024 : true
      ),
    photo: Yup.mixed()
      .test('fileType', 'Invalid file format', (value) => {
        if (!value) return true;
        return value && value.type && value.type.split('/')[0] === 'image';
      })
      .test('fileSize', 'File size should be between 5 KB and 25 MB', (value) =>
        value ? value.size >= 5 * 1024 && value.size <= 25 * 1024 * 1024 : true
      ),
  });

  const override = {
    display: "block",
    margin: "0 auto",
    borderColor: "#3b82f6",
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-blue-500/30">
      <Header />
      
      <div className="px-6 py-8 md:px-12 lg:px-20">
        {/* Header Section */}
        <div className="mb-12">
          <button
            onClick={() => navigate('/CandidateSchedule')}
            className="group flex items-center gap-3 text-slate-400 hover:text-blue-400 transition-colors mb-6"
          >
            <div className="w-10 h-10 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center group-hover:border-blue-500/30 group-hover:bg-blue-500/10 transition-all">
              <svg className="w-5 h-5 transform group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </div>
            <span className="text-sm font-medium">Back to Dashboard</span>
          </button>
          
          <div className="space-y-2">
            <h1 className="text-4xl font-bold text-white tracking-tight">
              Edit Profile
            </h1>
            <h2 className="text-slate-400 text-lg">
              Update your personal and professional details
            </h2>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          {loading ? (
          <div className="flex justify-center items-center py-20">
            <RiseLoader color={"#3b82f6"} loading={loading} css={override} size={15} />
          </div>
        ) : (
          <Formik
            initialValues={candidateProfile}
            validationSchema={signupschema}
            enableReinitialize={true}
            onSubmit={async (values) => {
              setLoading(true);
              const yourS3BucketName = "getscreenedstoragebucketf24ed-devget";

              try {
                let updatedProfile = { ...candidateProfile };

                if (values.photo) {
                  const photoKey = await Storage.put(
                    `${Date.now()}_${values.photo.name}`,
                    values.photo,
                    { contentType: "image/*" }
                  );
                  updatedProfile.photoURL = `https://${yourS3BucketName}.s3.amazonaws.com/public/${photoKey.key}`;
                }

                if (values.resume) {
                  const resumeKey = await Storage.put(
                    `${Date.now()}_${values.resume.name}`,
                    values.resume,
                    { contentType: "application/pdf" }
                  );
                  updatedProfile.resumeURL = `https://${yourS3BucketName}.s3.amazonaws.com/public/${resumeKey.key}`;
                }

                const mutationInput = {
                  id: candidateProfile.id,
                  fullName: values.fullName,
                  gender: values.gender,
                  mobileNumber: values.mobileNumber,
                  description: values.description,
                  matchedPercentage: values.matchedPercentage,
                  workStatus: values.workStatus,
                  currentCity: values.currentCity,
                  educationDetails: values.educationDetails,
                  keySkills: values.keySkills,
                  workPreference: values.workPreference,
                  preferredSalary: values.preferredSalary,
                  roleApplyFor: values.roleApplyFor,
                  address: values.address,
                  photoURL: updatedProfile.photoURL || values.photoURL,
                  resumeURL: updatedProfile.resumeURL || values.resumeURL,
                  workMode: values.workMode,
                  workCategory: values.workCategory,
                  githubURL: values.githubURL,
                  linkedinURL: values.linkedinURL,
                };

                await API.graphql(
                  graphqlOperation(updateCandidateProfile, { input: mutationInput })
                );

                navigate("/CandidateSchedule");
              } catch (error) {
                console.error("Error updating candidate profile:", error);
              } finally {
                setLoading(false);
              }
            }}
          >
            {({ values, errors, touched, handleChange, handleSubmit, setFieldValue, handleBlur }) => (
              <form onSubmit={handleSubmit} className="space-y-12">
                
                {/* Personal Information Section */}
                <div className="space-y-6">
                  <div className="border-b border-slate-800 pb-4">
                    <h2 className="text-xl font-semibold text-white">Personal Information</h2>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Full Name */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        placeholder="Enter your full name"
                        value={values.fullName || ''}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      />
                      {touched.fullName && errors.fullName && <p className="text-xs text-red-400 mt-1">{errors.fullName}</p>}
                    </div>
                    
                    {/* Gender */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Gender</label>
                      <select
                        name="gender"
                        onChange={handleChange}
                        value={values.gender || ''}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-lg text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      >
                        <option value="" className="bg-slate-900">Select Gender</option>
                        <option value="male" className="bg-slate-900">Male</option>
                        <option value="female" className="bg-slate-900">Female</option>
                        <option value="other" className="bg-slate-900">Other</option>
                      </select>
                    </div>
                    
                    {/* Mobile Number */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">
                        Mobile Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="mobileNumber"
                        placeholder="e.g. 9561471059"
                        value={values.mobileNumber || ''}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      />
                      {touched.mobileNumber && errors.mobileNumber && <p className="text-xs text-red-400 mt-1">{errors.mobileNumber}</p>}
                    </div>
                    
                    {/* Current City */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Current City</label>
                      <input
                        type="text"
                        name="currentCity"
                        placeholder="e.g. Pune"
                        value={values.currentCity || ''}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      />
                      {touched.currentCity && errors.currentCity && <p className="text-xs text-red-400 mt-1">{errors.currentCity}</p>}
                    </div>
                    
                    {/* Address */}
                    <div className="md:col-span-2 space-y-2">
                      <label className="text-sm font-medium text-slate-300">Address</label>
                      <input
                        type="text"
                        name="address"
                        placeholder="e.g. Hinjewadi Phase 1, Wakad, Pune, Maharashtra"
                        value={values.address || ''}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      />
                      {touched.address && errors.address && <p className="text-xs text-red-400 mt-1">{errors.address}</p>}
                    </div>
                    
                    {/* Description */}
                    <div className="md:col-span-2 space-y-2">
                      <label className="text-sm font-medium text-slate-300">About Me</label>
                      <textarea
                        name="description"
                        placeholder="Brief description about yourself"
                        value={values.description || ''}
                        onChange={handleChange}
                        rows={4}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all resize-none"
                      />
                      {touched.description && errors.description && <p className="text-xs text-red-400 mt-1">{errors.description}</p>}
                    </div>
                  </div>
                </div>

                {/* Professional Information Section */}
                <div className="space-y-6">
                  <div className="border-b border-slate-800 pb-4">
                    <h2 className="text-xl font-semibold text-white">Professional Information</h2>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Role Applying For */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">
                        Role Applying For <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="roleApplyFor"
                        placeholder="e.g. Senior Data Scientist"
                        value={values.roleApplyFor || ''}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      />
                      {touched.roleApplyFor && errors.roleApplyFor && <p className="text-xs text-red-400 mt-1">{errors.roleApplyFor}</p>}
                    </div>
                    
                    {/* Work Status */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">
                        Work Status <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="workStatus"
                        placeholder="e.g. Fresher or 3 Years Experience"
                        value={values.workStatus || ''}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      />
                      {touched.workStatus && errors.workStatus && <p className="text-xs text-red-400 mt-1">{errors.workStatus}</p>}
                    </div>
                    
                    {/* Key Skills */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">
                        Key Skills <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="keySkills"
                        placeholder="e.g. ReactJS, NextJS, Python"
                        value={values.keySkills || ''}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      />
                      {touched.keySkills && errors.keySkills && <p className="text-xs text-red-400 mt-1">{errors.keySkills}</p>}
                    </div>
                    
                    {/* Preferred Salary */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Expected Salary (Annual)</label>
                      <input
                        type="text"
                        name="preferredSalary"
                        placeholder="e.g. 500000"
                        value={values.preferredSalary || ''}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      />
                      {touched.preferredSalary && errors.preferredSalary && <p className="text-xs text-red-400 mt-1">{errors.preferredSalary}</p>}
                    </div>
                    
                    {/* Work Preference */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Preferred Work Location</label>
                      <input
                        type="text"
                        name="workPreference"
                        placeholder="e.g. Pune, Bangalore"
                        value={values.workPreference || ''}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      />
                      {touched.workPreference && errors.workPreference && <p className="text-xs text-red-400 mt-1">{errors.workPreference}</p>}
                    </div>
                    
                    {/* Education Details */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Education Details</label>
                      <input
                        type="text"
                        name="educationDetails"
                        placeholder="e.g. B.Tech in Computer Science"
                        value={values.educationDetails || ''}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      />
                      {touched.educationDetails && errors.educationDetails && <p className="text-xs text-red-400 mt-1">{errors.educationDetails}</p>}
                    </div>
                  </div>
                </div>

                {/* Work Preferences Section */}
                <div className="space-y-6">
                  <div className="border-b border-slate-800 pb-4">
                    <h2 className="text-xl font-semibold text-white">Work Preferences</h2>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Work Mode */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-slate-300">Work Mode</label>
                      <div className="flex flex-wrap gap-3">
                        {['Remote', 'Hybrid', 'Office'].map((mode) => (
                          <label
                            key={mode}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-full cursor-pointer transition-all border ${
                              values.workMode && values.workMode.includes(mode)
                                ? 'bg-blue-500/10 border-blue-500 text-blue-400'
                                : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'
                            }`}
                          >
                            <input
                              type="checkbox"
                              name="workMode"
                              value={mode}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              checked={values.workMode && values.workMode.includes(mode)}
                              className="sr-only"
                            />
                            <span className="text-sm font-medium">{mode}</span>
                          </label>
                        ))}
                      </div>
                      {touched.workMode && errors.workMode && (
                        <p className="text-xs text-red-400">{errors.workMode}</p>
                      )}
                    </div>

                    {/* Work Category */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-slate-300">Work Category</label>
                      <div className="flex flex-wrap gap-3">
                        {['Internship', 'FullTime', 'PartTime'].map((category) => (
                          <label
                            key={category}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-full cursor-pointer transition-all border ${
                              values.workCategory && values.workCategory.includes(category)
                                ? 'bg-emerald-500/10 border-emerald-500 text-emerald-400'
                                : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'
                            }`}
                          >
                            <input
                              type="checkbox"
                              name="workCategory"
                              value={category}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              checked={values.workCategory && values.workCategory.includes(category)}
                              className="sr-only"
                            />
                            <span className="text-sm font-medium">{category === 'FullTime' ? 'Full Time' : category === 'PartTime' ? 'Part Time' : category}</span>
                          </label>
                        ))}
                      </div>
                      {touched.workCategory && errors.workCategory && (
                        <p className="text-xs text-red-400">{errors.workCategory}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Social Links Section */}
                <div className="space-y-6">
                  <div className="border-b border-slate-800 pb-4">
                    <h2 className="text-xl font-semibold text-white">Social Links</h2>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* GitHub URL */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">GitHub URL</label>
                      <input
                        type="text"
                        name="githubURL"
                        placeholder="https://github.com/username"
                        value={values.githubURL || ''}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      />
                      {touched.githubURL && errors.githubURL && <p className="text-xs text-red-400 mt-1">{errors.githubURL}</p>}
                    </div>
                    
                    {/* LinkedIn URL */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">LinkedIn URL</label>
                      <input
                        type="text"
                        name="linkedinURL"
                        placeholder="https://linkedin.com/in/username"
                        value={values.linkedinURL || ''}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      />
                      {touched.linkedinURL && errors.linkedinURL && <p className="text-xs text-red-400 mt-1">{errors.linkedinURL}</p>}
                    </div>
                  </div>
                </div>

                {/* Documents Section */}
                <div className="space-y-6">
                  <div className="border-b border-slate-800 pb-4">
                    <h2 className="text-xl font-semibold text-white">Documents</h2>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Photo Upload */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-slate-300">Profile Photo</label>
                      <div className="flex items-center gap-4 p-4 bg-slate-900 border border-slate-800 rounded-lg">
                        {candidateProfile.photoURL ? (
                          <img
                            src={candidateProfile.photoURL}
                            alt="Profile"
                            className="w-16 h-16 rounded-full object-cover border-2 border-slate-700"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center text-slate-500">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                          </div>
                        )}
                        <div className="flex-1">
                          <input
                            type="file"
                            name="photo"
                            id="photo"
                            accept="image/*"
                            onChange={(event) => {
                              setFieldValue("photo", event.currentTarget.files[0]);
                            }}
                            className="hidden"
                          />
                          <label
                            htmlFor="photo"
                            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg cursor-pointer transition-colors"
                          >
                            Change Photo
                          </label>
                          {values.photo && (
                            <p className="text-xs text-emerald-400 mt-2">Selected: {values.photo.name}</p>
                          )}
                        </div>
                      </div>
                      {touched.photo && errors.photo && (
                        <p className="text-xs text-red-400">{errors.photo}</p>
                      )}
                    </div>

                    {/* Resume Upload */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-slate-300">Resume (PDF)</label>
                      <div className="p-4 bg-slate-900 border border-slate-800 rounded-lg">
                        {candidateProfile.resumeURL && (
                          <div className="flex items-center gap-2 text-sm text-blue-400 mb-3">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                            <span className="truncate max-w-[200px]">{candidateProfile.resumeURL.split("/").pop()}</span>
                          </div>
                        )}
                        <input
                          type="file"
                          name="resume"
                          id="resume"
                          accept=".pdf"
                          onChange={(event) => {
                            setFieldValue("resume", event.currentTarget.files[0]);
                          }}
                          className="hidden"
                        />
                        <label
                          htmlFor="resume"
                          className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg cursor-pointer transition-colors"
                        >
                          Upload New Resume
                        </label>
                        {values.resume && (
                          <p className="text-xs text-emerald-400 mt-2">Selected: {values.resume.name}</p>
                        )}
                      </div>
                      {touched.resume && errors.resume && (
                        <p className="text-xs text-red-400">{errors.resume}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-end pt-8 border-t border-slate-800">
                  <button
                    type="button"
                    onClick={() => navigate("/CandidateSchedule")}
                    className="px-6 py-2.5 text-slate-400 hover:text-white font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-8 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/20"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            )}
          </Formik>
        )}
        </div>
      </div>
    </div>
  );
};

export default EditCandidateProfileReg;
